import { useEffect, useId, useRef, useState } from "react";
import { recordingLinkResponseSchema } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { ApiRequestError, apiRequest } from "../api/http";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { Select } from "../design/components/Select";
import { messageForError } from "../i18n/errors";
import { formatClockDuration } from "../i18n/format";
import { useT, type Translate } from "../i18n/locale";
import type { TabContext } from "../session/tab-context";
import "./recording.css";

// «Ascolta» (piano M4, Task 12, D8; mock-up `scheda-lead.html`, `initLeadCard`): un pulsante che dice dove sei («Ascolta ·
// 6:12», «0:12 / 6:12» mentre ascolti, «Riprendi · 0:12 / 6:12», «Ascolta di nuovo · 6:12»), la «Velocità di ascolto» 1×,
// 1,5× e 2×, che vale anche se scelta prima, e un solo `<audio>` senza controlli suoi.
//
// L'indirizzo per ascoltare (`GET /api/calls/:callId/recording`, valido cinque minuti) si chiede soltanto quando si preme, e vive
// soltanto nella memoria di questo componente: nessuna cache delle query, nessuna memoria del browser. Dal 4 ottobre 2026 è di
// solito un indirizzo di Gioia365, relativo e dello stesso sito (`/api/recording-audio/<gettone>`), che il browser chiede con il
// cookie della sessione e il server gira dal fornitore; per l'audio copiato prima, quello firmato dello spazio. Se è scaduto
// (`expiresAt`, con un margine per l'orologio del browser) se ne chiede un altro e l'ascolto riparte da dov'era; se l'audio lo
// rifiuta (il server o lo spazio dicono già scaduto) mentre si ascolta, se ne chiede un altro una volta per ogni pressione; in
// pausa no, l'indirizzo si dimentica e lo chiede la pressione dopo. Un rifiuto del server
// dice il perché: il permesso, la registrazione che non c'è più, l'audio che adesso non si raggiunge.

/** Le velocità del mock-up; il valore è quello di `playbackRate`. */
const SPEEDS = [
  { value: "1", label: "recordings.speeds.normal" },
  { value: "1.5", label: "recordings.speeds.faster" },
  { value: "2", label: "recordings.speeds.fastest" },
] as const satisfies ReadonlyArray<{ value: string; label: MessageKey }>;
type Speed = (typeof SPEEDS)[number]["value"];

const isSpeed = (value: string): value is Speed => SPEEDS.some((speed) => speed.value === value);

/** Quanto prima della scadenza si chiede un indirizzo nuovo: l'orologio del browser non è quello del server. */
const EXPIRY_MARGIN_MS = 30_000;

type Phase = "idle" | "loading" | "playing" | "paused" | "ended";
type Link = { url: string; expiresAt: number };

/** Il perché di un indirizzo rifiutato: il permesso (`403`), la registrazione che non c'è più (`404`), altrimenti il codice. */
function refusal(t: Translate, error: unknown): string {
  if (error instanceof ApiRequestError && error.status === 403) return t("recordings.notAllowed");
  if (error instanceof ApiRequestError && error.status === 404) return t("recordings.gone");
  return messageForError(t, error);
}

const isAbort = (error: unknown) => error instanceof DOMException && error.name === "AbortError";

export type RecordingPlayerProps = {
  /** La scheda: l'azienda e la «Guarda come…» con cui si chiede l'indirizzo. */
  tab: TabContext;
  callId: string;
  /** La durata della registrazione, se il server la sa; quella esatta arriva con l'audio. */
  durationSeconds: number | null;
};

export function RecordingPlayer({ tab, callId, durationSeconds }: RecordingPlayerProps) {
  const t = useT();
  const speedId = useId();
  const audioRef = useRef<HTMLAudioElement>(null);
  const link = useRef<Link | null>(null);
  const request = useRef<AbortController | null>(null);
  /** Dove era l'ascolto, in secondi: da qui riparte con un indirizzo nuovo. */
  const position = useRef(0);
  /** Gli indirizzi nuovi che un errore dell'audio può ancora chiedere per questa pressione. */
  const retries = useRef(0);
  const [phase, setPhaseState] = useState<Phase>("idle");
  /** La fase anche fuori dal rendering: un evento dell'audio la legge quando arriva, non com'era all'ultimo rendering. */
  const phaseRef = useRef<Phase>("idle");
  const [seconds, setSeconds] = useState(0);
  /** La durata dell'audio arrivato, la più precisa; poi quella della chiamata e quella dell'indirizzo. */
  const [mediaDuration, setMediaDuration] = useState<number | null>(null);
  const [linkDuration, setLinkDuration] = useState<number | null>(null);
  const [speed, setSpeed] = useState<Speed>("1");
  const [problem, setProblem] = useState<string | null>(null);
  const duration = mediaDuration ?? durationSeconds ?? linkDuration;

  // Lasciata la pagina, l'ascolto si ferma e l'indirizzo si dimentica.
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      request.current?.abort();
      if (audio && link.current) {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      }
      link.current = null;
    };
  }, []);

  function setPhase(next: Phase) {
    phaseRef.current = next;
    setPhaseState(next);
  }

  /** L'indirizzo ancora valido, o uno nuovo dal server. */
  async function currentLink(): Promise<Link> {
    const known = link.current;
    if (known && Date.now() < known.expiresAt - EXPIRY_MARGIN_MS) return known;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const response = await apiRequest("GET", `/api/calls/${encodeURIComponent(callId)}/recording`, { schema: recordingLinkResponseSchema, tab, signal: controller.signal });
    const fresh = { url: response.url, expiresAt: Date.parse(response.expiresAt) };
    link.current = fresh;
    setLinkDuration(response.durationSeconds);
    return fresh;
  }

  function applySpeed(audio: HTMLAudioElement, value: Speed) {
    // `defaultPlaybackRate` resta anche quando l'indirizzo cambia e l'audio si ricarica.
    audio.defaultPlaybackRate = Number(value);
    audio.playbackRate = Number(value);
  }

  async function play() {
    const audio = audioRef.current;
    if (!audio) return;
    setPhase("loading");
    setProblem(null);
    let current: Link;
    try {
      current = await currentLink();
    } catch (error) {
      if (isAbort(error)) return;
      setProblem(refusal(t, error));
      setPhase(position.current > 0 ? "paused" : "idle");
      return;
    }
    if (audio.getAttribute("src") !== current.url) {
      audio.src = current.url;
      // Un indirizzo nuovo ricarica l'audio: si riparte da dov'era.
      audio.currentTime = position.current;
    }
    applySpeed(audio, speed);
    try {
      await audio.play();
      setPhase("playing");
    } catch (error) {
      // Il browser vuole un'altra pressione (l'indirizzo è arrivato troppo tardi dopo il clic): pronto a riprendere. Ogni altro
      // rifiuto arriva anche come errore dell'audio, che chiede un indirizzo nuovo.
      if (error instanceof DOMException && error.name === "NotAllowedError") setPhase("paused");
    }
  }

  function press() {
    if (phase === "loading") return;
    if (phase === "playing") {
      audioRef.current?.pause();
      setPhase("paused");
      return;
    }
    retries.current = 1;
    void play();
  }

  function changeSpeed(value: string) {
    if (!isSpeed(value)) return;
    setSpeed(value);
    if (audioRef.current) applySpeed(audioRef.current, value);
  }

  function audioFailed() {
    // Un errore senza indirizzo (l'audio appena svuotato) non riguarda l'ascolto.
    if (!link.current) return;
    link.current = null;
    // In pausa o a fine ascolto non si riparte da soli (il browser che rilegge l'audio con un indirizzo scaduto): la prossima
    // pressione chiede un indirizzo nuovo.
    if (phaseRef.current !== "loading" && phaseRef.current !== "playing") return;
    if (retries.current > 0) {
      retries.current -= 1;
      void play();
      return;
    }
    setProblem(t("recordings.playFailed"));
    setPhase(position.current > 0 ? "paused" : "idle");
  }

  const clock =
    duration === null ? formatClockDuration(seconds) : t("recordings.clock", { position: formatClockDuration(seconds), duration: formatClockDuration(duration) });
  let label: string;
  switch (phase) {
    case "loading":
      label = t("recordings.loading");
      break;
    case "playing":
      label = clock;
      break;
    case "paused":
      label = t("recordings.resume", { clock });
      break;
    case "ended":
      label = duration === null ? t("recordings.againShort") : t("recordings.again", { duration: formatClockDuration(duration) });
      break;
    default:
      label = duration === null ? t("recordings.listen") : t("recordings.listenFor", { duration: formatClockDuration(duration) });
  }

  return (
    <div className="g-recording-player">
      <Button
        className="g-recording-player__play"
        aria-label={phase === "playing" ? t("recordings.pause", { clock }) : undefined}
        aria-disabled={phase === "loading" ? "true" : undefined}
        onClick={press}
      >
        <Icon name={phase === "playing" ? "pause" : "play"} />
        {label}
      </Button>
      <Select
        id={speedId}
        label={t("recordings.speed")}
        hideLabel
        className="g-recording-player__speed"
        value={speed}
        onChange={changeSpeed}
        options={SPEEDS.map((option) => ({ value: option.value, label: t(option.label) }))}
      />
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- la registrazione di una telefonata non ha sottotitoli: la trascrizione arriva dopo WhatsApp (decisione del 4 ottobre 2026) */}
      <audio
        ref={audioRef}
        preload="none"
        onTimeUpdate={(event) => {
          position.current = event.currentTarget.currentTime;
          setSeconds(Math.floor(event.currentTarget.currentTime));
        }}
        onLoadedMetadata={(event) => {
          const value = event.currentTarget.duration;
          if (Number.isFinite(value)) setMediaDuration(Math.round(value));
        }}
        onPause={() => {
          if (phaseRef.current === "playing") setPhase("paused");
        }}
        onEnded={() => {
          position.current = 0;
          setSeconds(0);
          setPhase("ended");
        }}
        onError={audioFailed}
      />
      {problem ? (
        <Reason tone="warning" role="alert" className="g-recording-player__problem">
          {problem}
        </Reason>
      ) : null}
    </div>
  );
}
